import React from 'react';
import { Link, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import usePWA from '../hooks/usePWA';
import Icon from './Icon';
import UserProfile from './UserProfile';
import TransactionFAB from './TransactionFAB';
import PropTypes from 'prop-types';

/**
 * 경로와 페이지 헤더 타이틀 매핑
 * @type {Record<string, string>}
 */
const PAGE_TITLES = {
  '/dashboard': '대시보드',
  '/calendar': '달력 조회',
  '/transactions': '거래 내역',
  '/transactions/add': '거래 추가',
  '/export': '데이터 내보내기',
  '/analytics': '상세 분석',
  '/settings': '설정',
};

/**
 * FAB 버튼을 표시할 경로 목록
 * @type {string[]}
 */
const FAB_VISIBLE_PATHS = ['/dashboard', '/calendar', '/transactions'];

/**
 * 사이드바 네비게이션 구조 정의
 * 웹은 조회/분석에 집중하는 전략.
 * - 거래 추가: FAB 버튼으로 대체
 * - 고정 거래: 조회 + 관리 가능
 * - 데이터 내보내기: CSV/JSON/월별 리포트 다운로드
 * - 목표 관리, AI 분석, 대화형 AI, 알림: 모바일 전용
 */
const NAV_SECTIONS = [
  {
    title: '가계부',
    items: [
      { path: '/dashboard', icon: 'space_dashboard', label: '대시보드' },
      { path: '/calendar', icon: 'calendar_today', label: '달력 조회' },
      { path: '/transactions', icon: 'receipt_long', label: '거래 내역' },
    ],
  },
  {
    title: '분석',
    items: [
      { path: '/analytics', icon: 'insights', label: '상세 분석' },
      { path: '/export', icon: 'file_upload', label: '데이터 내보내기' },
    ],
  },
  {
    title: '기타',
    items: [
      { path: '/settings', icon: 'settings', label: '설정' },
    ],
  },
];

/**
 * AppLayout — 보호 라우트의 공통 레이아웃 컴포넌트
 *
 * 기존 App.js의 사이드바 + 메인 콘텐츠 구조를 별도 컴포넌트로 분리.
 * - useAuth()로 user 정보 접근
 * - useLocation()으로 현재 경로 확인하여 active 네비게이션 표시
 * - 사이드바 네비게이션은 <Link>로 경로 이동
 * - <Outlet />으로 자식 라우트 렌더링
 * - FAB 버튼: /dashboard, /calendar, /transactions 경로에서만 표시
 * - 모바일 메뉴 토글 기능 유지
 *
 * @returns {React.ReactElement}
 */
const AppLayout = () => {
  const { handleLogout } = useAuth();
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = React.useState(false);
  const { isInstallable, installPWA } = usePWA();

  const currentPath = location.pathname;
  const pageTitle = PAGE_TITLES[currentPath] || '';
  const showFAB = FAB_VISIBLE_PATHS.includes(currentPath);

  /**
   * 모바일 메뉴를 닫는 핸들러
   */
  const closeMobileMenu = React.useCallback(() => {
    setIsMobileMenuOpen(false);
  }, []);

  const toggleMobileMenu = React.useCallback(() => {
    setIsMobileMenuOpen(prev => !prev);
  }, []);

  return (
    <div className="app-layout">
      {/* 모바일 메뉴 토글 버튼 */}
      <button
        className="mobile-menu-toggle"
        onClick={toggleMobileMenu}
        aria-label="메뉴 열기"
      >
        <Icon name="menu" />
      </button>

      {/* 모바일 오버레이 */}
      {isMobileMenuOpen && (
        <div
          className="mobile-overlay open"
          onClick={closeMobileMenu}
          role="presentation"
        />
      )}

      {/* 사이드바 */}
      <aside className={`sidebar ${isMobileMenuOpen ? 'open' : ''}`}>
        {/* 브랜드 영역 */}
        <div className="sidebar-brand">
          <Link to="/dashboard" className="sidebar-brand-icon" aria-label="MoneyMind 홈">
            <img src="/logo192.png" alt="MoneyMind" className="sidebar-brand-logo" />
          </Link>
          <span className="sidebar-brand-text">MoneyMind</span>
        </div>

        {/* 네비게이션 */}
        <nav className="sidebar-nav">
          {NAV_SECTIONS.map((section, sectionIndex) => (
            <React.Fragment key={section.title}>
              {sectionIndex > 0 && <div className="nav-divider" />}
              <div className="nav-section">
                {section.items.map((item) => (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`nav-item ${currentPath === item.path ? 'active' : ''}`}
                    onClick={closeMobileMenu}
                    aria-label={item.label}
                  >
                    <Icon name={item.icon} />
                    <span className="nav-label">{item.label}</span>
                  </Link>
                ))}
              </div>
            </React.Fragment>
          ))}
        </nav>

        {/* 푸터 */}
        <div className="sidebar-footer">
          {isInstallable && (
            <button onClick={installPWA} className="nav-item" aria-label="앱 설치">
              <Icon name="install_mobile" />
              <span className="nav-label">앱 설치</span>
            </button>
          )}
          <button onClick={handleLogout} className="nav-item" aria-label="로그아웃">
            <Icon name="logout" />
            <span className="nav-label">로그아웃</span>
          </button>
        </div>
      </aside>

      {/* 메인 콘텐츠 */}
      <main className="main-content">
        {/* 페이지 헤더 */}
        <div className="page-header">
          <h2>{pageTitle}</h2>
          <UserProfile />
        </div>

        {/* 콘텐츠 영역 — 자식 라우트가 여기에 렌더링됨 */}
        <div className="content-area">
          <Outlet />
        </div>
      </main>

      {/* FAB 버튼 — 대시보드, 달력, 거래 내역 페이지에서만 표시 */}
      {showFAB && <TransactionFAB />}
    </div>
  );
};

export default AppLayout;
